import { cn } from "@/lib/utils";

/**
 * A pure HTML/CSS phone mockup with a customizable screen slot.
 * Used across module sections and the screenshots carousel.
 */
export function PhoneMockup({ children, className, tone = "light" }) {
  return (
    <div
      className={cn(
        "relative mx-auto w-[280px] h-[560px] rounded-[2.75rem] shadow-2xl",
        "border-[12px] border-[#1A1918]",
        tone === "light" ? "bg-white" : "bg-[#111]",
        className,
      )}
    >
      {/* Notch */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-[#1A1918] rounded-b-2xl z-20" />
      {/* Side buttons */}
      <div className="absolute -left-[14px] top-24 w-[3px] h-10 bg-[#1A1918] rounded-l" />
      <div className="absolute -left-[14px] top-40 w-[3px] h-16 bg-[#1A1918] rounded-l" />
      <div className="absolute -right-[14px] top-32 w-[3px] h-20 bg-[#1A1918] rounded-r" />
      {/* Screen */}
      <div className="w-full h-full rounded-[2rem] overflow-hidden relative">
        {children}
      </div>
    </div>
  );
}

/* Individual mock screens */

export function MockScreenNeeds() {
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="pt-9 px-4 pb-3 bg-[#FF5A00] text-white">
        <div className="text-[10px] opacity-80">Bengaluru, Koramangala</div>
        <div className="text-base font-bold">Local Needs</div>
      </div>
      <div className="p-3 space-y-2 overflow-hidden flex-1">
        {[
          { t: "Need a plumber urgently", b: "₹500 • 2km away", c: "#FFF3ED" },
          { t: "Tutor for Class 10 Maths", b: "₹8,000/mo", c: "#F1F5FF" },
          { t: "Weekend house cleaning", b: "₹1,200", c: "#F0FDF4" },
          { t: "Bike delivery – 1 pickup", b: "₹200", c: "#FFF7ED" },
        ].map((n, i) => (
          <div key={i} className="rounded-xl p-3 border border-[#EEE9E3]" style={{ background: n.c }}>
            <div className="text-[11px] font-semibold text-[#1A1918]">{n.t}</div>
            <div className="text-[10px] text-[#78716C] mt-1">{n.b}</div>
          </div>
        ))}
      </div>
      <div className="p-3">
        <div className="rounded-full bg-[#FF5A00] text-white text-[11px] font-semibold py-2 text-center">
          + Post a Need
        </div>
      </div>
    </div>
  );
}

export function MockScreenJobs() {
  return (
    <div className="h-full flex flex-col bg-[#FFFBF5]">
      <div className="pt-9 px-4 pb-3">
        <div className="text-base font-bold text-[#1A1918]">Jobs near you</div>
        <div className="mt-2 flex gap-1.5 text-[10px]">
          <span className="px-2 py-0.5 rounded-full bg-[#FF5A00] text-white">Part-time</span>
          <span className="px-2 py-0.5 rounded-full bg-white border border-[#EEE9E3]">Full-time</span>
          <span className="px-2 py-0.5 rounded-full bg-white border border-[#EEE9E3]">Remote</span>
        </div>
      </div>
      <div className="px-3 space-y-2 flex-1">
        {[
          { r: "Barista", c: "Third Wave Coffee", p: "₹18k • 2.1km" },
          { r: "Delivery Rider", c: "LocalMart", p: "₹22k • 1.4km" },
          { r: "Graphic Designer", c: "Studio Nine", p: "₹32k • 3.0km" },
        ].map((j, i) => (
          <div key={i} className="rounded-xl bg-white p-3 border border-[#EEE9E3]">
            <div className="text-[11px] font-semibold">{j.r}</div>
            <div className="text-[10px] text-[#78716C]">{j.c}</div>
            <div className="text-[10px] text-[#FF5A00] font-semibold mt-1">{j.p}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MockScreenShop() {
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="pt-9 h-32 bg-gradient-to-br from-[#FF8A3D] to-[#FF5A00] relative">
        <div className="absolute bottom-3 left-3 text-white">
          <div className="text-[10px] opacity-80">Verified Shop</div>
          <div className="text-sm font-bold">Sharma General Store</div>
        </div>
      </div>
      <div className="p-3 space-y-2 flex-1">
        <div className="flex gap-2">
          <div className="flex-1 rounded-lg bg-[#FFF3ED] p-2 text-center">
            <div className="text-[10px] font-bold text-[#FF5A00]">4.8★</div>
            <div className="text-[9px] text-[#78716C]">Rating</div>
          </div>
          <div className="flex-1 rounded-lg bg-[#FFF3ED] p-2 text-center">
            <div className="text-[10px] font-bold text-[#FF5A00]">1.2k</div>
            <div className="text-[9px] text-[#78716C]">Customers</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {["#FFE4CC", "#FFD8A8", "#FFC078", "#FFD8A8", "#FFE4CC", "#FFC078"].map((c, i) => (
            <div key={i} className="aspect-square rounded-md" style={{ background: c }} />
          ))}
        </div>
      </div>
      <div className="p-3 flex gap-2">
        <div className="flex-1 rounded-full bg-[#FF5A00] text-white text-[10px] font-semibold py-2 text-center">Call</div>
        <div className="flex-1 rounded-full border border-[#FF5A00] text-[#FF5A00] text-[10px] font-semibold py-2 text-center">Chat</div>
      </div>
    </div>
  );
}

export function MockScreenMarketplace() {
  const items = [
    { c: "#FFE0B2", label: "Bike" },
    { c: "#FFCCBC", label: "Sofa" },
    { c: "#FFD180", label: "iPhone" },
    { c: "#F8BBD0", label: "Books" },
    { c: "#C5E1A5", label: "Cycle" },
    { c: "#B3E5FC", label: "TV" },
  ];
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="pt-9 px-4 pb-3">
        <div className="text-base font-bold">Marketplace</div>
        <div className="mt-2 rounded-full bg-[#FFF3ED] text-[10px] text-[#78716C] px-3 py-1.5">🔍 Search near you</div>
      </div>
      <div className="grid grid-cols-2 gap-2 px-3 flex-1">
        {items.map((it, i) => (
          <div key={i} className="rounded-xl overflow-hidden border border-[#EEE9E3]">
            <div className="h-16" style={{ background: it.c }} />
            <div className="p-1.5">
              <div className="text-[10px] font-semibold">{it.label}</div>
              <div className="text-[9px] text-[#FF5A00] font-bold">₹{(i + 1) * 1250}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MockScreenWallet() {
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="pt-9 px-4 pb-4 bg-[#1A1918] text-white">
        <div className="text-[10px] opacity-70">Wallet Balance</div>
        <div className="text-2xl font-bold mt-1">₹ 1,240</div>
        <div className="text-[10px] opacity-70 mt-1">+40 credits available</div>
      </div>
      <div className="p-3 grid grid-cols-3 gap-2">
        {["Top Up", "Coupons", "History"].map((a, i) => (
          <div key={i} className="rounded-xl bg-[#FFF3ED] py-2 text-center text-[10px] font-semibold text-[#FF5A00]">{a}</div>
        ))}
      </div>
      <div className="px-3 space-y-1.5 flex-1">
        <div className="text-[10px] font-semibold text-[#78716C] px-1 mt-1">Recent</div>
        {[
          { t: "Post unlock", a: "-20" },
          { t: "Referral bonus", a: "+50" },
          { t: "Top up", a: "+500" },
        ].map((t, i) => (
          <div key={i} className="flex justify-between items-center bg-[#FFFBF5] rounded-lg px-2.5 py-1.5">
            <span className="text-[10px]">{t.t}</span>
            <span className={`text-[10px] font-bold ${t.a.startsWith("+") ? "text-emerald-600" : "text-[#FF5A00]"}`}>{t.a}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MockScreenSOS() {
  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-[#FEF2F2] to-white">
      <div className="pt-9 px-4 pb-3">
        <div className="text-base font-bold text-[#B91C1C]">SOS Safety</div>
        <div className="text-[10px] text-[#78716C]">Alert your trusted circle</div>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <div className="w-32 h-32 rounded-full bg-red-500 text-white flex items-center justify-center shadow-2xl relative">
          <div className="absolute inset-0 rounded-full bg-red-500/40 animate-ping" />
          <span className="font-bold text-lg relative">SOS</span>
        </div>
      </div>
      <div className="p-3">
        <div className="text-[10px] font-semibold text-[#78716C] mb-1">Trusted contacts</div>
        <div className="flex gap-1.5">
          {["A", "R", "M", "S"].map((l, i) => (
            <div key={i} className="w-8 h-8 rounded-full bg-[#FF5A00] text-white text-xs flex items-center justify-center font-bold">{l}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MockScreenBlood() {
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="pt-9 px-4 pb-3">
        <div className="text-base font-bold text-[#B91C1C]">Blood Requests</div>
        <div className="text-[10px] text-[#78716C]">Urgent near you</div>
      </div>
      <div className="px-3 space-y-2 flex-1">
        {[
          { g: "O+", h: "Apollo Hospital", d: "2.3 km" },
          { g: "B-", h: "Fortis", d: "3.1 km" },
          { g: "A+", h: "Manipal", d: "5.6 km" },
        ].map((b, i) => (
          <div key={i} className="rounded-xl p-3 border border-red-100 bg-red-50 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500 text-white font-bold flex items-center justify-center text-xs">{b.g}</div>
            <div className="flex-1">
              <div className="text-[11px] font-semibold">{b.h}</div>
              <div className="text-[10px] text-[#78716C]">{b.d}</div>
            </div>
            <div className="text-[9px] font-bold text-red-600">URGENT</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MockScreenHome() {
  return (
    <div className="h-full flex flex-col bg-[#FDFBF7]">
      <div className="pt-9 px-4 pb-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] text-[#78716C]">Good morning,</div>
          <div className="text-sm font-bold">Rohit ✨</div>
        </div>
        <div className="w-8 h-8 rounded-full bg-[#FF5A00] text-white text-xs flex items-center justify-center font-bold">R</div>
      </div>
      <div className="px-3">
        <div className="rounded-full bg-white border border-[#EEE9E3] text-[10px] text-[#78716C] px-3 py-2">🔍 Search anything local</div>
      </div>
      <div className="p-3 grid grid-cols-4 gap-2">
        {[
          { l: "Needs", c: "#FFF3ED" },
          { l: "Jobs", c: "#F1F5FF" },
          { l: "Shops", c: "#FFFBEA" },
          { l: "Market", c: "#F0FDF4" },
          { l: "Blood", c: "#FEF2F2" },
          { l: "SOS", c: "#FFF7ED" },
          { l: "Wallet", c: "#F5F3FF" },
          { l: "More", c: "#F9FAFB" },
        ].map((t, i) => (
          <div key={i} className="rounded-xl p-1.5 text-center" style={{ background: t.c }}>
            <div className="w-6 h-6 rounded-full bg-white mx-auto" />
            <div className="text-[9px] mt-1 font-semibold">{t.l}</div>
          </div>
        ))}
      </div>
      <div className="mx-3 mt-1 rounded-xl bg-gradient-to-br from-[#FF8A3D] to-[#FF5A00] p-3 text-white">
        <div className="text-[10px] opacity-90">Featured</div>
        <div className="text-xs font-bold">Local Diwali Sale</div>
      </div>
    </div>
  );
}

export function MockScreenProfile() {
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="pt-9 pb-4 bg-gradient-to-br from-[#FF5A00] to-[#FF8A3D] text-white text-center">
        <div className="w-16 h-16 rounded-full bg-white/20 border-2 border-white mx-auto flex items-center justify-center text-xl font-bold">R</div>
        <div className="mt-2 text-sm font-bold">Rohit Sharma</div>
        <div className="text-[10px] opacity-80">+91 98••••34</div>
      </div>
      <div className="p-3 space-y-1.5 flex-1">
        {["My Posts", "My Shop", "Saved", "Referrals", "Settings", "Help"].map((it, i) => (
          <div key={i} className="flex justify-between items-center rounded-lg border border-[#EEE9E3] px-3 py-2 text-[11px] font-medium">
            <span>{it}</span>
            <span className="text-[#FF5A00]">›</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MockScreenChat() {
  const msgs = [
    { m: "Hi, is the sofa still available?", me: false },
    { m: "Yes, it is. Want to see it today?", me: true },
    { m: "Sure! Can I come by 6pm?", me: false },
    { m: "Perfect. See you then 👍", me: true },
  ];
  return (
    <div className="h-full flex flex-col bg-[#FDFBF7]">
      <div className="pt-9 px-4 pb-3 bg-white border-b border-[#EEE9E3] flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-[#FF5A00] text-white text-xs flex items-center justify-center font-bold">A</div>
        <div>
          <div className="text-[11px] font-bold">Aditi</div>
          <div className="text-[9px] text-emerald-600">Online</div>
        </div>
      </div>
      <div className="flex-1 p-3 space-y-2 overflow-hidden">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.me ? "justify-end" : "justify-start"}`}>
            <div className={`text-[10px] px-2.5 py-1.5 rounded-2xl max-w-[75%] ${m.me ? "bg-[#FF5A00] text-white rounded-br-sm" : "bg-white border border-[#EEE9E3] rounded-bl-sm"}`}>
              {m.m}
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 flex gap-2 border-t border-[#EEE9E3] bg-white">
        <div className="flex-1 rounded-full bg-[#FFF3ED] text-[10px] text-[#78716C] px-3 py-2">Type a message…</div>
        <div className="w-8 h-8 rounded-full bg-[#FF5A00]" />
      </div>
    </div>
  );
}
